import { jsPDF } from "jspdf";
import JSZip from "jszip";
import logoUrl from "../assets/logo.png";
import { API_BASE_URL } from "../api/client";

const PAGE = {
  width: 210,
  height: 297,
  left: 22,
  right: 22,
  top: 22,
  bottom: 22
};

const TERMS = [
  "1. By signing below, I acknowledge my acceptance of and/or certification of the following information: Approval of the Submitted Work and Acknowledgment of the Role of Corresponding Author: I have personally reviewed and given final approval of the version submitted. With the exception of previously published work or data, which is clearly acknowledged in the manuscript and for which appropriate written permission is included as part of the submission, to my knowledge, neither the manuscript nor its data have been previously published (except in abstract) or are currently under consideration for publication by any other publication. I agree that the corresponding author (named above) shall be the sole correspondent with the Editorial Office on all matters related to this submission. In the event of acceptance, I designate the corresponding author as the responsible party for all communications with the journal's publisher related to this work, including review and correction of the typeset proof. I understand that once a manuscript is submitted, no substantial changes to the content will be allowed. I further understand that no changes will be entertained regarding the addition or deletion of authors, amendments to affiliations, change of authorship, or any such revisions once the paper is accepted.",
  "2. Validity of Work: The manuscript represents valid work. Neither this manuscript nor one with substantially similar content under my/our authorship has been published or is being considered for publication elsewhere, except as described in the covering letter. I confirm that I/we have secured all necessary permissions for any copyrighted material (such as quotes, photographs, or other visual content) included in the Contribution that I/we do not own, and I/we have acknowledged all relevant sources.",
  "3. Authorship Contribution: I have participated sufficiently in the work to take public responsibility for all or part of the content, and have made substantive intellectual contributions to the submitted work in the form of: 1) conception and design, and/or acquisition of data, and/or analysis and interpretation of data; AND 2) drafting the article, and/or revising it critically for important intellectual content. I have identified all persons, and their employer(s), who have contributed to or have substantively edited the submitted work.",
  "4. Conflict of Interest, Disclosures, Financial, and Material Support: To my knowledge, all of my possible conflicts of interest and those of my co-authors, financial or otherwise, including direct or indirect financial or personal relationships, interests, and affiliations, whether or not directly related to the subject of the paper, are listed in the appropriate sections of this manuscript. Disclosure includes, but is not limited to, grants or research funding, employment, affiliations, patents (in preparation, filed, or granted), inventions, speakers' bureaus, honoraria, consultancies, royalties, stock options/ownership, or expert testimony. If an author has no conflicts of interest to declare, this must be explicitly stated.",
  "5. Plagiarism, Fabrication, Duplication of Data or Scientific Misconduct, and Competing Financial Interest Related to Journal Policy: The journal follows COPE guidelines in handling the above matters [www.publicationethics.org], and I agree to all the terms and conditions of the journal and its policies.",
  "6. Publication Agreement: This Agreement will grant to Manuscript TechnoMedia LLP (the 'Publisher'), the owners of the Journal identified above, a non-exclusive commercial license to produce, publish, sell, and sub-license my/our article ('Article') and any accompanying abstract or supplemental material (all materials collectively referenced as the 'Contribution'), in all languages and all formats through any medium of communication for the full legal term of copyright (and any renewals) throughout the world. The Publisher will publish the Contribution under a Creative Commons license. Where the selected license is CC BY-NC or other Creative Commons non-commercial license, the Publisher will receive exclusive commercial rights to the Article and non-exclusive commercial rights to the abstract and supplemental material.",
  "7. Copyright: The copyright to the Contribution is owned by the author(s). I represent and warrant that the copyright to the Contribution is owned by me/us. While copyright remains with the authors, I hereby authorize the Publisher to act on my/our behalf to defend the copyright.",
  "8. Article Processing Charges: I agree to pay the manuscript charges upon acceptance, and I am aware of the charges prior to the submission of this manuscript.",
  "9. Termination: I understand that the Publisher, at its sole and absolute discretion, may decide that the Contribution is not suitable for publication in the Journal.",
  "10. Publishing Ethics & Legal Adherence: I understand that if any Contribution is found to violate this Agreement, it may be withdrawn from publication or corrected.",
  "11. General Provisions: I understand that this Agreement shall be governed by and construed in accordance with the laws of India. Any disputes arising from or related to this Agreement shall be subject to the exclusive jurisdiction of the courts located in Bangalore, Karnataka. The parties agree to first attempt to resolve any dispute amicably. If unresolved, it will be referred to arbitration as per Indian law. This Agreement is signed electronically, and such signatures will be treated the same as handwritten ones."
];

const toDateStamp = (value) => {
  const date = value ? new Date(value) : new Date();
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

const splitStateFromAddress = (address) => {
  const parts = String(address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return "NA";
  }
  return parts[parts.length - 2] || "NA";
};

const resolveAuthorsText = (submission) => {
  const authors = Array.isArray(submission.authors) ? submission.authors : [];
  if (!authors.length) {
    return submission.name ? `${submission.name}, ${submission.email || ""};` : "-";
  }
  const validAuthors = authors
    .filter((author) => author?.name?.trim())
    .map((author) => {
      const name = author?.name?.trim() || "Unknown";
      const email = author?.email?.trim();
      return email ? `${name}, ${email};` : `${name};`;
    });
  return validAuthors.length ? validAuthors.join(" ") : "-";
};

const resolveCorrespondence = (submission) => {
  const authors = Array.isArray(submission.authors) ? submission.authors : [];
  const selected =
    authors.find((author) => author?.id && author.id === submission.correspondingAuthorId) ||
    authors.find((author) => author?.isCorresponding);
  return {
    name: selected?.name?.trim() || submission.correspondenceName || submission.name || "NA",
    email: selected?.email?.trim() || submission.correspondenceEmail || submission.email || "NA"
  };
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read image file."));
    reader.readAsDataURL(file);
  });

const fetchUrlAsDataUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load image from ${url}`);
  }
  const blob = await response.blob();
  if (!String(blob.type || "").startsWith("image/")) {
    throw new Error("Loaded file is not an image.");
  }
  return fileToDataUrl(blob);
};

const resolveSignatureDataUrl = async (submission) => {
  if (submission.signatureDataUrl) {
    return submission.signatureDataUrl;
  }
  if (submission.signature instanceof File || submission.signature instanceof Blob) {
    return fileToDataUrl(submission.signature);
  }
  if (typeof submission.signature === "string" && submission.signature) {
    const normalizedPath = submission.signature.replace(/\\/g, "/");
    const fullUrl = normalizedPath.startsWith("http")
      ? normalizedPath
      : `${API_BASE_URL}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
    try {
      return await fetchUrlAsDataUrl(fullUrl);
    } catch {
      return "";
    }
  }
  return "";
};

const drawCenteredText = (doc, text, y, size, bold = false) => {
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(size);
  doc.text(text, PAGE.width / 2, y, { align: "center" });
};

const drawHeader = (doc, logoDataUrl) => {
  const startY = PAGE.top - 4;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", PAGE.left, startY, 26, 11);
  }
  drawCenteredText(doc, "MANUSCRIPT TECHNOMEDIA LLP", PAGE.top + 1, 15, true);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11.5);
  doc.text("CONTRIBUTORS AGREEMENT FORM", PAGE.width / 2, PAGE.top + 7, {
    align: "center",
    charSpace: 0.6
  });
  doc.setLineWidth(0.25);
  doc.line(PAGE.left, PAGE.top + 11, PAGE.width - PAGE.right, PAGE.top + 11);
  return { y: PAGE.top + 18 };
};

const addHeading = (doc, cursor, heading) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.3);
  doc.text(heading, PAGE.left, cursor.y);
  return { y: cursor.y + 7 };
};

const addUnderlinedField = (doc, cursor, label, value) => {
  const pageContentWidth = PAGE.width - PAGE.left - PAGE.right;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.2);
  const labelText = `${label} `;
  const labelWidth = doc.getTextWidth(labelText);
  const valueX = PAGE.left + labelWidth + 1.2;
  const lineWidth = Math.max(30, pageContentWidth - (labelWidth + 1.2));
  const text = String(value || "NA");

  doc.text(labelText, PAGE.left, cursor.y);

  doc.setFont("helvetica", "normal");
  const lines = doc.splitTextToSize(text, lineWidth - 1);
  const lineHeight = 5.4;
  lines.forEach((line, index) => {
    const y = cursor.y + index * lineHeight;
    doc.text(line, valueX, y);
    doc.setLineWidth(0.2);
    doc.line(valueX, y + 0.8, valueX + lineWidth, y + 0.8);
  });

  return { y: cursor.y + Math.max(1, lines.length) * lineHeight + 1.5 };
};

const ensurePageSpace = (doc, cursor, requiredHeight) => {
  if (cursor.y + requiredHeight <= PAGE.height - PAGE.bottom) {
    return cursor;
  }
  doc.addPage();
  return { y: PAGE.top };
};

const addJustifiedParagraph = (doc, cursor, text) => {
  const width = PAGE.width - PAGE.left - PAGE.right;
  const lines = doc.splitTextToSize(text, width);
  const lineHeight = 5.3;
  doc.text(lines, PAGE.left, cursor.y, {
    maxWidth: width,
    align: "justify",
    lineHeightFactor: 1.52
  });
  return { y: cursor.y + lines.length * lineHeight + 3.2 };
};

export async function buildSubmissionPdfBlob(submission) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const correspondence = resolveCorrespondence(submission);
  const logoDataUrl = await fetchUrlAsDataUrl(logoUrl).catch(() => "");
  const signatureDataUrl = await resolveSignatureDataUrl(submission);
  const authorText = resolveAuthorsText(submission);
  const ipv4 = submission.ipv4Address || submission.ipv4 || "Not Available";
  const state = submission.state || splitStateFromAddress(submission.address);
  const country = submission.countryResolved || submission.country || "NA";

  let cursor = drawHeader(doc, logoDataUrl);

  cursor = addHeading(doc, cursor, "ARTICLE DETAILS:");
  cursor = addUnderlinedField(doc, cursor, "author name:", authorText);
  cursor = addUnderlinedField(doc, cursor, "correspondence author name:", correspondence.name);
  cursor = addUnderlinedField(doc, cursor, "correspondence email:", correspondence.email);
  cursor = addUnderlinedField(doc, cursor, "phone number:", `${submission.countryCode || ""} ${submission.phone || ""}`.trim());
  cursor = addUnderlinedField(doc, cursor, "work address:", submission.address || "NA");
  cursor = addUnderlinedField(doc, cursor, "country:", submission.country || "NA");

  cursor.y += 3;
  cursor = ensurePageSpace(doc, cursor, 32);
  cursor = addHeading(doc, cursor, "JOURNAL DETAILS:");
  cursor = addUnderlinedField(doc, cursor, "manuscript id:", submission.msId || "NA");
  cursor = addUnderlinedField(doc, cursor, "manuscript title:", submission.msTitle || "NA");
  cursor = addUnderlinedField(doc, cursor, "journal name:", submission.journalName || "NA");

  cursor.y += 3;
  cursor = ensurePageSpace(doc, cursor, 20);
  cursor = addHeading(doc, cursor, "TERMS AND CONDITIONS:");
  cursor.y += 1.2;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.2);

  TERMS.forEach((item) => {
    cursor = ensurePageSpace(doc, cursor, 26);
    cursor = addJustifiedParagraph(doc, cursor, item);
    cursor.y += 1.8;
  });

  cursor = ensurePageSpace(doc, cursor, 56);
  const signatureLabelY = cursor.y + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Signature:", PAGE.width - 64, signatureLabelY);
  let signatureLineY = signatureLabelY + 16;

  if (signatureDataUrl) {
    const sigY = signatureLabelY + 4;
    doc.addImage(signatureDataUrl, "PNG", PAGE.width - 64, sigY, 45, 18, undefined, "FAST");
    doc.setLineWidth(0.2);
    signatureLineY = sigY + 19.2;
    doc.line(PAGE.width - 64, signatureLineY, PAGE.width - 19, signatureLineY);
  } else {
    const sigY = signatureLabelY + 16;
    signatureLineY = sigY;
    doc.setLineWidth(0.2);
    doc.line(PAGE.width - 64, signatureLineY, PAGE.width - 19, signatureLineY);
  }

  const stampBaseY = signatureLineY + 7;
  const dateValue = toDateStamp(submission.createdAt);
  const lineStartX = PAGE.left;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.6);
  doc.setTextColor(0, 0, 0);
  const part1 = "This form is submitted from IPv4 address ";
  const part2 = ` (${state}, ${country}) Date: `;
  const w1 = doc.getTextWidth(part1);
  const wIp = doc.getTextWidth(ipv4);
  const w2 = doc.getTextWidth(part2);
  doc.text(part1, lineStartX, stampBaseY);
  doc.setTextColor(185, 28, 28);
  doc.text(ipv4, lineStartX + w1, stampBaseY);
  doc.setTextColor(0, 0, 0);
  doc.text(part2, lineStartX + w1 + wIp, stampBaseY);
  doc.setTextColor(185, 28, 28);
  doc.text(dateValue, lineStartX + w1 + wIp + w2, stampBaseY);
  doc.setTextColor(0, 0, 0);

  return doc.output("blob");
}

const triggerDownload = (url, name) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
};

export async function getSubmissionPdfSource(submission) {
  const fileName =
    String(submission?.pdfFileName || "").trim() ||
    `submission-${submission.msId || Date.now()}.pdf`;
  const blob = await buildSubmissionPdfBlob(submission);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const url = URL.createObjectURL(blob);
  return {
    url,
    bytes,
    fileName,
    cleanup: () => URL.revokeObjectURL(url)
  };
}

export function getSubmissionPdfSourceFromServer(submission, accessToken) {
  const fileName =
    String(submission?.pdfFileName || "").trim() ||
    `submission-${submission.msId || Date.now()}.pdf`;
  if (!submission?._id || !accessToken) {
    return null;
  }
  const resolveBackendBase = () => {
    if (API_BASE_URL) {
      return API_BASE_URL;
    }
    if (typeof window !== "undefined") {
      const { protocol, hostname, port, origin } = window.location;
      if (port === "3000") {
        return `${protocol}//${hostname}:5000`;
      }
      return origin;
    }
    return "";
  };
  const backendBase = resolveBackendBase();
  if (!backendBase) {
    return null;
  }
  const encodedToken = encodeURIComponent(accessToken);
  const encodedId = encodeURIComponent(submission._id);
  const base = `${backendBase}/api/submissions/${encodedId}/pdf?accessToken=${encodedToken}`;
  return {
    fileName,
    url: `${base}&download=false`,
    downloadUrl: `${base}&download=true`,
    cleanup: null
  };
}

export async function getSubmissionPdfBlob(submission, accessToken) {
  const source = getSubmissionPdfSourceFromServer(submission, accessToken);
  if (source?.url) {
    const response = await fetch(source.url);
    if (!response.ok) {
      throw new Error("Unable to fetch PDF from server.");
    }
    const blob = await response.blob();
    return { blob, fileName: source.fileName };
  }

  const localSource = await getSubmissionPdfSource(submission);
  const response = await fetch(localSource.url);
  const blob = await response.blob();
  if (localSource.cleanup) {
    localSource.cleanup();
  }
  return { blob, fileName: localSource.fileName };
}

export async function downloadSubmissionPdf(submission, accessToken) {
  const { blob, fileName } = await getSubmissionPdfBlob(submission, accessToken);
  const url = URL.createObjectURL(blob);
  triggerDownload(url, fileName);
  URL.revokeObjectURL(url);
}

export async function downloadSubmissionsZip(submissions, zipName = "submissions.zip", accessToken) {
  const zip = new JSZip();
  for (const submission of submissions || []) {
    const { blob, fileName } = await getSubmissionPdfBlob(submission, accessToken);
    const bytes = await blob.arrayBuffer();
    zip.file(fileName, bytes);
  }
  const zipBlob = await zip.generateAsync({ type: "blob" });
  const zipUrl = URL.createObjectURL(zipBlob);
  triggerDownload(zipUrl, zipName);
  URL.revokeObjectURL(zipUrl);
}
